/**
 * Unit Picker Modal
 * Lets a landlord pick an available unit to start a new lease for — surfaced from the
 * Leases page's "Create Lease" entry point. Selecting a unit hands off to Unit Detail
 * Page's existing lease-creation entry logic via the `?createLease=true` query param.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../Modal';
import { EmptyState } from '../layout/index';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { listUnits, type UnitWithRelations } from '../../lib/api';
import { formatUnitLabel } from '@resida/shared';

interface UnitPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (unitId: string) => void;
}

interface PropertyGroup {
  propertyId: string;
  propertyName: string;
  units: UnitWithRelations[];
}

export function UnitPickerModal({ isOpen, onClose, onSelect }: UnitPickerModalProps) {
  const [units, setUnits] = useState<UnitWithRelations[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadUnits = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { units: loadedUnits } = await listUnits();
      setUnits(loadedUnits);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load units');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadUnits();
    }
  }, [isOpen, loadUnits]);

  const availableUnits = units.filter((u) => u.occupancy === 'available');

  // Group by property, preserving the order properties first appear in the filtered list.
  const groups: PropertyGroup[] = [];
  for (const unit of availableUnits) {
    const propertyId = unit.propertyId ?? unit.property?.id ?? '';
    const propertyName = unit.propertyName ?? unit.property?.name ?? 'Property';
    let group = groups.find((g) => g.propertyId === propertyId);
    if (!group) {
      group = { propertyId, propertyName, units: [] };
      groups.push(group);
    }
    group.units.push(unit);
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Select a Unit" data-testid="unit-picker-modal">
      {error && (
        <div
          className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
          role="alert"
        >
          <p className="text-destructive">{error}</p>
          <button
            onClick={loadUnits}
            type="button"
            data-testid="unit-picker-retry-button"
            className="mt-1 inline-flex min-h-[44px] items-center text-sm font-medium text-destructive underline"
          >
            Retry
          </button>
        </div>
      )}

      {isLoading && (
        <div className="space-y-3" data-testid="unit-picker-loading">
          <Skeleton className="h-4 w-24" />
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      )}

      {!isLoading && !error && availableUnits.length === 0 && (
        <EmptyState
          title="No available units"
          description="All units are currently leased."
          action={
            <Link to="/units">
              <Button data-testid="unit-picker-add-unit-button">Add a Unit</Button>
            </Link>
          }
        />
      )}

      {!isLoading && !error && availableUnits.length > 0 && (
        <div className="max-h-[400px] overflow-auto">
          {groups.map((group) => (
            <div key={group.propertyId} className="mb-4 last:mb-0">
              <h3 className="mb-2 text-sm font-semibold text-foreground">{group.propertyName}</h3>
              <div className="flex flex-col gap-2">
                {group.units.map((unit) => (
                  <button
                    key={unit.id}
                    type="button"
                    onClick={() => onSelect(unit.id)}
                    data-testid={`unit-picker-option-${unit.id}`}
                    className="w-full min-h-[44px] rounded border border-border bg-card p-4 text-left cursor-pointer transition-colors hover:bg-accent"
                  >
                    {formatUnitLabel(unit.unitNumber) ?? (unit.address1 || (unit.propertyName ?? unit.property?.name ?? 'Property'))}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

export default UnitPickerModal;
